import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  styled,
  Typography,
  useTheme
} from "@mui/material"
import React, { useEffect, useRef, useState } from "react"
import CloseIcon from "@mui/icons-material/Close"
import { useSelector } from "react-redux"
import FoodIcon from "assets/icons/food.svg"
import CocktailIcon from "assets/icons/cocktail.svg"
import WineIcon from "assets/icons/wine.svg"
import VIPIcon from "assets/icons/vip.svg"
import SoundIcon from "assets/icons/sound.svg"
import "./MobilePreview.scss"
import DidYouKnow from "./components/didYouKnow"
import History from "./components/history"
import Details from "./components/details"
import PlayArrow from "@mui/icons-material/PlayArrowRounded"
import Pause from "@mui/icons-material/PauseRounded"

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    backgroundColor: theme.palette.background.paper,
    maxWidth: 700,
    width: "100%",
    borderRadius: 12,
    overflow: "hidden"
  },
  "& .MuiDialogContent-root": {
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    padding: 0,
    boxSizing: "border-box",
    flex: 1,
    display: "flex",
    flexDirection: "column"
  }
}))

const MobilePreview = ({ open, handleClose, data = {}, inline = false }) => {
  const theme = useTheme()
  const [menuPreview, setMenuPreview] = useState({})
  const [expanded, setExpanded] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const audioRef = useRef(null)
  const { menuPreviewData: previewData } = useSelector((store) => store?.marketing?.menu ?? {})
  const menuPreviewData = data && Object.keys(data)?.length > 0 ? data : previewData

  const styles = {
    closeBtn: {
      color: theme.palette.text.primary,
      position: "absolute",
      top: "15px",
      right: "15px",
      backgroundColor: theme.palette.background.main
    }
  }

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      audio
        .play()
        .then(() => {
          setIsPlaying(true)
        })
        .catch((err) => {
          console.error("Audio play error:", err)
        })
    }
  }

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const updateProgress = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        const percent = (audio.currentTime / audio.duration) * 100
        setProgress(percent)
      }
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setProgress(0)
      setExpanded(false)
    }

    audio.addEventListener("loadedmetadata", updateProgress)
    audio.addEventListener("timeupdate", updateProgress)
    audio.addEventListener("ended", handleEnded)

    return () => {
      audio.removeEventListener("loadedmetadata", updateProgress)
      audio.removeEventListener("timeupdate", updateProgress)
      audio.removeEventListener("ended", handleEnded)
    }
  }, [expanded])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    if (expanded) {
      audio
        .play()
        .then(() => {
          setIsPlaying(true)
        })
        .catch((err) => {
          console.error("Auto-play error:", err)
        })
    } else {
      audio.pause()
      audio.currentTime = 0
      setIsPlaying(false)
      setProgress(0)
    }
  }, [expanded])

  useEffect(() => {
    if (!menuPreviewData) return

    if (!menuPreviewData?.data_to_display) {
      const updatedData = [
        menuPreviewData?.place && {
          filedName: "Place",
          value: menuPreviewData.place,
          show_bullets: false,
          can_be_expanded: false
        },
        menuPreviewData?.most_famous_wines && {
          filedName: "Most Famous Wines",
          value: menuPreviewData.most_famous_wines,
          show_bullets: false,
          can_be_expanded: false
        },
        menuPreviewData?.white_grape_varietals && {
          filedName: "White Grape Varietals",
          value: menuPreviewData.white_grape_varietals,
          show_bullets: false,
          can_be_expanded: false
        },
        menuPreviewData?.red_grape_varietals && {
          filedName: "Red Grape Varietals",
          value: menuPreviewData.red_grape_varietals,
          show_bullets: false,
          can_be_expanded: false
        },
        menuPreviewData?.terroir && {
          filedName: "Terroir",
          value: menuPreviewData.terroir,
          show_bullets: false,
          can_be_expanded: false
        },
        menuPreviewData?.famous_vineyards && {
          filedName: "Famous Vineyards",
          value: menuPreviewData.famous_vineyards,
          show_bullets: false,
          can_be_expanded: false
        },
        menuPreviewData?.unique_facts && {
          filedName: "Unique Facts",
          value: menuPreviewData.unique_facts,
          show_bullets: false,
          can_be_expanded: false
        }
      ].filter(Boolean)

      setMenuPreview({ data_to_display: updatedData })
    } else {
      const updated = [
        ...(menuPreviewData?.price
          ? [{ filedName: "Price", value: `$ ${menuPreviewData.price}` }]
          : []),

        ...menuPreviewData.data_to_display.filter((item) => item.filedName !== "Menu Description")
      ]

      setMenuPreview({ data_to_display: updated })
    }
  }, [menuPreviewData])

  useEffect(() => {
    setExpanded(false)
  }, [open])

  const renderContent = () => (
    <Box sx={{ overflowY: "auto", padding: inline ? 0 : "35px" }}>
      {!inline && (
        <Box className="d-flex justify-content-end">
          <IconButton onClick={() => handleClose()} sx={styles.closeBtn}>
            <CloseIcon className="icon-btn" />
          </IconButton>
        </Box>
      )}
      <Box className="mobile-preview__container mobile_preview mt-5" justifySelf="center">
        <Box>
          <Grid
            className="mobile-preview__hero-section"
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: `url(${
                menuPreviewData?.image_url || menuPreviewData?.image
              }) lightgray 50% / cover no-repeat`
            }}>
            <div className="mobile-preview__gradient-overlay-top" />
            <div className="mobile-preview__gradient-overlay-bottom" />
            <img
              src={
                menuPreviewData?.bar_category
                  ? CocktailIcon
                  : menuPreviewData?.country || menuPreviewData?.wine_category
                  ? WineIcon
                  : menuPreviewData?.meal_period?.name === "VIP"
                  ? VIPIcon
                  : FoodIcon
              }
              className="mobile-preview__meal-icon"
            />
          </Grid>
          <Grid
            item
            container
            alignItems="center"
            justifyContent="space-between"
            className="mobile-preview__divider-section"
            wrap="nowrap">
            <div className="mobile-preview__divider-line flex-grow" />
            <Typography className="mobile-preview__meal-period no-wrap">
              {menuPreviewData?.meal_period?.name
                ? menuPreviewData?.meal_period?.name
                : menuPreviewData?.category?.meal_period
                ? menuPreviewData?.category?.meal_period
                : menuPreviewData?.bar_category
                ? "Cocktail"
                : menuPreviewData?.spirit_category?.name
                ? menuPreviewData?.spirit_category?.name
                : "Wine"}
            </Typography>
            <div className="mobile-preview__divider-line flex-grow" />
          </Grid>
        </Box>

        {/* Content Section */}
        <Box className="mobile-preview__content d-flex flex-column align-items-center justify-content-center mb-2">
          <Grid item mt={2.5} mb={3.5}>
            <Typography className="mobile-preview__menu-name">{menuPreviewData?.name}</Typography>
          </Grid>
          {menuPreviewData?.menu_description ? (
            <Grid item container justifyContent="center">
              <Typography variant="body2Regular" className="mobile-preview__menu-description">
                {Array.isArray(menuPreviewData?.menu_description)
                  ? menuPreviewData.menu_description.join(", ")
                  : menuPreviewData?.menu_description}
              </Typography>
            </Grid>
          ) : null}
          {!!menuPreviewData?.soundbite_url && (
            <Box className={`mobile-preview__soundbite ${expanded ? "expanded" : "collapsed"}`}>
              {expanded ? (
                <Box className="mobile-preview__soundbite-player">
                  <IconButton
                    size="small"
                    onClick={() => togglePlay()}
                    className="mobile-preview__soundbite-btn">
                    {isPlaying ? (
                      <Pause style={{ color: "white" }} />
                    ) : (
                      <PlayArrow style={{ color: "white" }} />
                    )}
                  </IconButton>

                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    className="mobile-preview__soundbite-progress"
                  />

                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      setExpanded(false)
                      if (audioRef.current) {
                        audioRef.current.pause()
                        audioRef.current.currentTime = 0
                      }
                      setIsPlaying(false)
                      setProgress(0)
                    }}
                    className="mobile-preview__soundbite-btn">
                    <CloseIcon style={{ color: "white" }} />
                  </IconButton>

                  <audio ref={audioRef} src={menuPreviewData?.soundbite_url} preload="auto" />
                </Box>
              ) : (
                <Box
                  className="mobile-preview__soundbite-mini"
                  onClick={(e) => {
                    e.stopPropagation()
                    setExpanded(true)
                  }}>
                  <img src={SoundIcon} className="mobile-preview__soundbite-icon" alt="sound" />
                  <Typography className="mobile-preview__soundbite-text">SOUNDBITE</Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Grid item container className="mobile-preview__separator">
          <div className="mobile-preview__separator-line" />
        </Grid>

        <Details menuPreview={menuPreview} />

        {menuPreviewData?.item_history?.title && <History data={menuPreviewData} />}

        {!!menuPreviewData?.did_you_know?.[0]?.title && (
          <DidYouKnow data={menuPreviewData?.did_you_know} />
        )}
      </Box>
    </Box>
  )

  if (inline) {
    return renderContent()
  }

  return (
    <StyledDialog open={open} className="mobile-preview-dialog">
      <DialogContent>{renderContent()}</DialogContent>
    </StyledDialog>
  )
}

export default MobilePreview
